import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

// Prefix for this module based on folder structure and file name
class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.dealer-franchisee.quotes.views.FranchiseeQuoteView.';

  public static readonly deletingQuoteItem = `${Id.prefix}deletingQuoteItem`;
  public static readonly gettingUpdatesFromSupplier = `${Id.prefix}gettingUpdatesFromSupplier`;
  public static readonly supplierProcessingComplete = `${Id.prefix}supplierProcessingComplete`;
  public static readonly quoteItemDeleted = `${Id.prefix}quoteItemDeleted`;
  public static readonly unableToDeleteSelectedQuoteItem = `${Id.prefix}unableToDeleteSelectedQuoteItem`;
  public static readonly closeExistingSupportRequestEditor = `${Id.prefix}closeExistingSupportRequestEditor`;
  public static readonly frameConfigRequired = `${Id.prefix}frameConfigRequired`;
  public static readonly pricingBasedOnPaymentProfile = `${Id.prefix}pricingBasedOnPaymentProfile`;
  public static readonly shopDrawingPlural = `${Id.prefix}shopDrawingPlural`;
  public static readonly supplierSupport = `${Id.prefix}supplierSupport`;
  public static readonly termsConditions = `${Id.prefix}termsConditions`;
  public static readonly quoteCalculations = `${Id.prefix}quoteCalculations`;
  public static readonly requestSupport = `${Id.prefix}requestSupport`;

  // Report captions
  public static readonly clientQuoteAPDF = `${Id.prefix}clientQuoteAPDF`;
  public static readonly clientQuoteBNoItemPricePDF = `${Id.prefix}clientQuoteBNoItemPricePDF`;
  public static readonly clientQuoteCNoItemPriceNoDimPDF = `${Id.prefix}clientQuoteCNoItemPriceNoDimPDF`;
  public static readonly clientQuoteGNoPricePDF = `${Id.prefix}clientQuoteGNoPricePDF`;
  public static readonly dealerQuoteEIternalPDF = `${Id.prefix}dealerQuoteEIternalPDF`;
  public static readonly dealerQuoteHIternalPDF = `${Id.prefix}dealerQuoteHIternalPDF`;
  public static readonly clientQuoteSummaryPDF = `${Id.prefix}clientQuoteSummaryPDF`;
  public static readonly quoteDataExportJson = `${Id.prefix}quoteDataExportJson`;
  public static readonly quoteDataExportXml = `${Id.prefix}quoteDataExportXml`;
  public static readonly generateReports = `${Id.prefix}generateReports`;
  public static readonly downloadReports = `${Id.prefix}downloadReports`;

  // Add new key for 'Preparing to delete'
  public static readonly preparingToDelete = `${Id.prefix}preparingToDelete`;
  public static readonly openQuoteItemWithActiveSupportRequests = `${Id.prefix}openQuoteItemWithActiveSupportRequests`;
}

export class LangFranchiseeQuoteView {
  static openQuoteItemWithActiveSupportRequests(arg0: { tickets: string }) {
    return interpolate(Id.openQuoteItemWithActiveSupportRequests, arg0);
  }
  private constructor() {}

  public static deletingQuoteItem(params: { title: string }): string {
    return interpolate(Id.deletingQuoteItem, params);
  }

  public static gettingUpdatesFromSupplier(): string {
    return interpolate(Id.gettingUpdatesFromSupplier);
  }

  public static supplierProcessingComplete(): string {
    return interpolate(Id.supplierProcessingComplete);
  }

  public static quoteItemDeleted(): string {
    return interpolate(Id.quoteItemDeleted);
  }

  public static unableToDeleteSelectedQuoteItem(): string {
    return interpolate(Id.unableToDeleteSelectedQuoteItem);
  }

  public static closeExistingSupportRequestEditor(): string {
    return interpolate(Id.closeExistingSupportRequestEditor);
  }

  public static frameConfigRequired(): string {
    return interpolate(Id.frameConfigRequired);
  }

  public static pricingBasedOnPaymentProfile(): string {
    return interpolate(Id.pricingBasedOnPaymentProfile);
  }

  public static shopDrawingPlural(): string {
    return interpolate(Id.shopDrawingPlural);
  }

  public static supplierSupport(): string {
    return interpolate(Id.supplierSupport);
  }

  public static termsConditions(): string {
    return interpolate(Id.termsConditions);
  }

  public static quoteCalculations(): string {
    return interpolate(Id.quoteCalculations);
  }

  public static requestSupport(): string {
    return interpolate(Id.requestSupport);
  }

  // Report captions
  public static clientQuoteAPDF(notGenerated: boolean): string {
    return interpolate(Id.clientQuoteAPDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static clientQuoteBNoItemPricePDF(notGenerated: boolean): string {
    return interpolate(Id.clientQuoteBNoItemPricePDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static clientQuoteCNoItemPriceNoDimPDF(notGenerated: boolean): string {
    return interpolate(Id.clientQuoteCNoItemPriceNoDimPDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static clientQuoteGNoPricePDF(notGenerated: boolean): string {
    return interpolate(Id.clientQuoteGNoPricePDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static dealerQuoteEIternalPDF(notGenerated: boolean): string {
    return interpolate(Id.dealerQuoteEIternalPDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static dealerQuoteHIternalPDF(notGenerated: boolean): string {
    return interpolate(Id.dealerQuoteHIternalPDF, {}, notGenerated ? 'notGenerated' : undefined);
  }

  public static clientQuoteSummaryPDF(notGenerated: boolean): string {
    return interpolate(Id.clientQuoteSummaryPDF, {}, notGenerated ? 'notGenerated' : undefined);
  }
  public static quoteDataExportJson(): string {
    return interpolate(Id.quoteDataExportJson);
  }
  public static quoteDataExportXml(): string {
    return interpolate(Id.quoteDataExportXml);
  }
  public static generateReports(): string {
    return interpolate(Id.generateReports);
  }
  public static downloadReports(): string {
    return interpolate(Id.downloadReports);
  }

  // Add new method for 'Preparing to delete'
  public static preparingToDelete(): string {
    return interpolate(Id.preparingToDelete);
  }
}

export class FranchiseeQuoteViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Dialogs and status
    register.add(
      Id.deletingQuoteItem,
      'Deleting {{title}}',
      'Dialog title for deleting a quote item, with the item title interpolated'
    );
    register.add(
      Id.gettingUpdatesFromSupplier,
      'Getting updates from supplier...',
      'Status message shown while getting updates from supplier'
    );
    register.add(
      Id.supplierProcessingComplete,
      'Supplier processing complete.',
      'Status message shown when supplier processing is complete'
    );
    register.add(Id.quoteItemDeleted, 'Quote item deleted.', 'Status message shown when a quote item has been deleted');
    register.add(
      Id.unableToDeleteSelectedQuoteItem,
      'Unable to delete selected quote item.',
      'Error message shown when a quote item cannot be deleted'
    );
    register.add(
      Id.closeExistingSupportRequestEditor,
      'Please close an existing support request editor before opening a new one.',
      'Message shown when user tries to open too many support request editors'
    );
    register.add(
      Id.frameConfigRequired,
      'Frame configuration is required before generating a supplier quote.',
      'Message shown when frame config is required'
    );
    register.add(
      Id.pricingBasedOnPaymentProfile,
      'Pricing is based on the payment profile. Please select a client type.',
      'Message shown when pricing is based on payment profile'
    );
    register.add(Id.shopDrawingPlural, 'Shop Drawings', 'Label for shop drawing documents (plural)');
    register.add(Id.supplierSupport, 'Supplier Support', 'Tab/page caption for supplier support');
    register.add(Id.requestSupport, 'Request Support', 'Button caption for requesting support');
    // Register new string for 'Preparing to delete'
    register.add(Id.preparingToDelete, 'Preparing to delete', 'Header for preparing to delete');
    // Report captions (with notGenerated interpolation)
    register
      .add(Id.clientQuoteAPDF, 'Full Detail', 'Caption for Client Quote A PDF report, with notGenerated flag')
      .addSegment('notGenerated', 'Full Detail (not generated)');
    register
      .add(
        Id.clientQuoteBNoItemPricePDF,
        'No Line Item Price',
        'Caption for Client Quote B No Item Price PDF report, with notGenerated flag'
      )
      .addSegment('notGenerated', 'No Line Item Price (not generated)');
    register
      .add(
        Id.clientQuoteCNoItemPriceNoDimPDF,
        'With Item Price, No Dimensions',
        'Caption for Client Quote C No Item Price No Dim PDF report, with notGenerated flag'
      )
      .addSegment('notGenerated', 'With Item Price, No Dimensions (not generated)');
    register
      .add(
        Id.clientQuoteGNoPricePDF,
        'No Price',
        'Caption for Client Quote G No Price PDF report, with notGenerated flag'
      )
      .addSegment('notGenerated', 'No Price (not generated)');
    register
      .add(
        Id.dealerQuoteEIternalPDF,
        'Supplier Cost, Dealer Internal View',
        'Caption for Dealer Quote E Internal PDF report, with notGenerated flag'
      )
      .addSegment('notGenerated', 'Supplier Cost, Dealer Internal View (not generated)');
    register
      .add(
        Id.dealerQuoteHIternalPDF,
        'Dealer Internal View, Multiplier',
        'Caption for Dealer Quote H Internal PDF report, with notGenerated flag'
      )
      .addSegment('notGenerated', 'Dealer Internal View, Multiplier (not generated)');
    register
      .add(Id.clientQuoteSummaryPDF, 'Summary', 'Caption for Client Quote Summary PDF report, with notGenerated flag')
      .addSegment('notGenerated', 'Summary (not generated)');

    // No notGenerated segment for either export: they are written once when the quote is issued
    // and are never generated on demand, so the entry is simply absent until the file exists.
    register.add(Id.quoteDataExportJson, 'Data Export (JSON)', 'Caption for the JSON data export download');
    register.add(Id.quoteDataExportXml, 'Data Export (XML)', 'Caption for the XML data export download');
    register.add(
      Id.generateReports,
      'Generate Reports',
      'Heading in the reports menu when the reports are generated on demand'
    );
    register.add(
      Id.downloadReports,
      'Download Reports',
      'Heading in the reports menu when the stored reports are downloaded'
    );
    register.add(Id.termsConditions, "{{glossary 'terms-conditions'}}", 'Tab captions for terms and conditions');
    register.add(
      Id.quoteCalculations,
      "{{glossary 'quote'}} Calculations",
      'Tab caption for the quote calculations page'
    );
    register.add(
      Id.openQuoteItemWithActiveSupportRequests,
      `This {{glossary 'quote-item'}} has active {{glossary 'support-request:plural'}}:
      
      {{tickets}}
      
      Changing this item may impact on the ability of the {{glossary 'supplier'}} to effectively respond to your requests.

      Are you sure you want to open this item for editing?
      
      If you do, please ensure you communicate any changes to the {{glossary 'supplier'}}. We will update the effected {{glossary 'support-request:plural'}} to reflect the changes made.`,

      'Warning message shown when trying to open a quote item that has active support requests'
    );
  }
}
